import { useState, useCallback } from 'react';
import { useCatalog } from '../hooks/useCatalog';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import { KeywordData } from '../contexts/CatalogContext';
import { logger } from '../utils/logger';
import { resolveCatalogAliIdForRequest } from '../utils/resolveCatalogAliIdForRequest';

export const useCatalogKeywords = (catalogItemId?: string, ownerId?: string | null) => {
  const { saveKeywords, getKeywords, error } = useCatalog();
  // Per-catalog wiring (PR 4d): when the wizard is active under one of the
  // user's owned add-on catalogs, the keywords-save fallback must mint under
  // that add-on rather than defaulting to primary. Delegated flows (ownerId
  // truthy) skip the param — the backend routes to the delegator's primary
  // via the owner_id query. See resolveCatalogAliIdForRequest for the rule.
  const { activeCatalogAliId, activeCatalogOwnerId } = useActiveCatalog();
  const catalogAliId = resolveCatalogAliIdForRequest(activeCatalogAliId, activeCatalogOwnerId);
  const [keywords, setKeywords] = useState<KeywordData | null>(null);
  const [localLoading, setLocalLoading] = useState(false);

  const loadKeywords = useCallback(async (id?: string) => {
    const itemId = id || catalogItemId;
    if (!itemId) return;

    setLocalLoading(true);
    try {
      const data = await getKeywords(itemId, ownerId);
      setKeywords(data);
      return data;
    } catch (err) {
      logger.error('Failed to load keywords', err);
      throw err;
    } finally {
      setLocalLoading(false);
    }
  }, [catalogItemId, getKeywords, ownerId]);

  const updateKeywords = useCallback(async (data: KeywordData, id?: string): Promise<{ aliCatalogNumber?: string }> => {
    const itemId = id || catalogItemId;
    if (!itemId) throw new Error('No catalog item ID provided');

    setLocalLoading(true);
    try {
      const result = await saveKeywords(itemId, data, ownerId, catalogAliId);
      // Return the result so caller can use the generated catalog number
      return result;
    } catch (err) {
      logger.error('Failed to save keywords', err);
      throw err;
    } finally {
      setLocalLoading(false);
    }
  }, [catalogItemId, saveKeywords, ownerId, catalogAliId]);

  return {
    keywords,
    loadKeywords,
    updateKeywords,
    isLoading: localLoading,
    error
  };
};
